import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SG_SURV_ITM_LIST, SF_SURV_ITM_INFO, SG_SURV_ITM_LANG_LIST } from "./services/PtliSurvItmMgmtService";
import { PTLI0418001Q } from "../qustnr/PTLI0418001Q";
import { PTLI0419001Q } from "../ques/PTLI0419001Q";
import { PTLI0419005Q } from "../ques/PTLI0419005Q";

/*
 * Survey Item Edit
 * !== 설문 항목 수정 ==!
 */

export const PTLI0420003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const { quesItmId } = useParams();
    const langCodes = ["KO", "EN", "TZ"];

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survItmInfo: useForm({
            defaultSchema: SF_SURV_ITM_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survItmLangList: useGrid({
            defaultSchema: SG_SURV_ITM_LANG_LIST
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvItmInfo: useFetch({
            api: (data) => APIS.getSurvItmInfo(quesItmId),
            enabled: !!quesItmId,
            onSuccess: (data) => {

                const { content } = data.survItmInfo || {};
                if (comnUtils.isEmpty(content)) {
                modal.openModal({ content: "msg.ptl.00694" }); // 비정상 접근입니다.
                    navigate(URLS.ptli0420001q);
                return;
                }

                // 1) 기본 필드 세팅 (qestnrId, quesId, quesTpCd, quesOrd 등)
                form.survItmInfo.setValues(content);

                // 2) 다국어 필드 세팅 (quesCnKO/EN/TZ)
                const { survQuesLangList } = content;
                    if (!comnUtils.isEmpty(survQuesLangList)) {
                        survQuesLangList.forEach((row: any) => {
                            const { langCd, quesCn } = row;
                            if (!comnUtils.isEmpty(langCd)) {
                                form.survItmInfo.setValue(`quesCn${langCd}`, quesCn ?? "");
                            }
                    });
                }
            },
            onError: (error) => {},
            showToast: true,
        }),

        updateSurvItmInfo: useFetch({
            api: (info: any) => APIS.updateSurvItmInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00059",
                    onConfirm: () => {
                        navigate(URLS.ptli0420001q);
                    },
                }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => {
                modal.openModal({
                    content: "msg.ptl.00837", // This survey has already commenced and cannot be edited.
                });
            },
        }),
    };
    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onUpdateBtnClick: async () => {
            const data = form.survItmInfo.getValues();

            if (!comnUtils.isEmpty(form.survItmInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.survItmInfo.handleSubmit(
                () => {
                    grid.survItmLangList.clearData();
                    
                    langCodes.forEach((langCd) => {
                        const quesCn = form.survItmInfo.getValue(`quesCn${langCd}`); // quesCnEN/TZ/KO
                        if (!comnUtils.isEmpty(quesCn)) {
                            grid.survItmLangList.addRow({
                                langCd,
                                quesCn, 
                            });
                        }
                    });
                    if (grid.survItmLangList.getData().length === 0) {
                        modal.openModal({ content: "error occurs" /* 최소 1개 언어 입력 요청 */ });
                        return;
                    }

                    const langList = grid.survItmLangList.getData();

                    const data = form.survItmInfo.getValues();

                    const payload = {
                        ...data,
                        survItmLangList: langList, 
                    };

                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateSurvItmInfo.fetch(payload);
                        },
                        close: false,
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getSurvItmInfo.fetch();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmInfo.schema.qustnrTtle}
                                readOnly="true"
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmInfo.setValue("qustnrTtle", item.qustnrTtle);
                                                form.survItmInfo.setValue("qestnrId", item.qestnrId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0418001Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmInfo.schema.quesCn}
                                readOnly="true"
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const qestnrId = form.survItmInfo.watch("qestnrId"); // 설문 선택에서 세팅된 값
                                        if (!qestnrId) {
                                            toast.showToast({ type: "warning", content: "msg.ptl.00835" }); // Please select a Questionnaire to begin.
                                            return;
                                        }
                                        const modalCallback = async (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmInfo.setValue("quesCn", item.quesCn);
                                                form.survItmInfo.setValue("quesId", item.quesId);

                                                try {
                                                    const { data } = await APIS.getNextItmOrd(item.quesId); // ← axios 응답
                                                    form.survItmInfo.setValue("itmOrd", String(data.nextOrd)); // ← 문자열로
                                                } catch (e) {
                                                    toast.showToast({ type: "warning", content: "msg.00006" });
                                                }
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "lg",
                                            content: <PTLI0419005Q qestnrId={qestnrId} callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmOrd} readOnly={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmCnEN} />
                            <Group.Control {...form.survItmInfo.schema.itmCnTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmInfo.schema.itmCnKO} />
                        </Group.Row>
                        
                    </Group.Section>
                    <Group.Section hidden={true}>
                            {" "}
                            <Grid {...grid.survItmLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button
                        role={"list"}
                        onClick={() => {
                            navigate(URLS.ptli0420001q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.onUpdateBtnClick}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
